import {
    MeshBasicMaterial,
    DoubleSide,
    TextureLoader, 
    RepeatWrapping
} from 'da-three';

export default class CWSPLScene {
    constructor(studio) {
        this.studio = studio;

        if (this.studio.config.project.projectID === 'paperroll-nt') {
            // found PL NT Project
            this.isPLNT = true;
            this.studio.dispatcher.addEventListener("objectComplete", this.onObjectComplete.bind(this));
            this.studio.dispatcher.addEventListener('animationState', this.onAnimation.bind(this));
            this.studio.dispatcher.addEventListener('beforeRender', this.onRender.bind(this));
        }

        if (this.studio.config.project.projectID === 'paperroll-nt-leds') { 
            this.isPLNTLED = true;
            this.studio.dispatcher.addEventListener('animationState', this.onLEDAnimation.bind(this));
            this.studio.dispatcher.addEventListener("objectComplete", this.onLEDObjectComplete.bind(this));
        }

        if (this.studio.config.project.projectID === 'paperroll-nt-parts') {
            this.isPLNTParts = true;
            this.studio.dispatcher.addEventListener('animationState', this.onPartsAnimation.bind(this));
            this.studio.dispatcher.addEventListener("objectComplete", this.onPartsObjectComplete.bind(this));
        }

        if (this.isPLNT || this.isPLNTLED) {
            // tex
            const loader = new TextureLoader();
            const towelTex = loader.load('https://studio.alchemisten.de/static/projects/cws/paperroll-nt/paper_roll_towel.jpg');
            towelTex.flipY = false;
            towelTex.wrapS = RepeatWrapping;
            towelTex.wrapT = RepeatWrapping;
            this.towelTex = towelTex;
        }
        
        this.caseTransparentConditions = [
            { from: 6.2, to: 11.85 },
            { from: 18.68, to: 26 },
            { from: 30.77, to: 34.20 }
        ];

        this.isTransparent = false;

        this.interp = this.studio.highlightMan.interpolateTo;
        this.lastRenderTime = 0;
        this.autoPlay = this.studio.config.features.useAnimations.default;
        this.begin = true;
    }

    onPartsAnimation() {
        if (this.begin) {
            this.begin = false;
            this.studio.animMan.animationToggle(false);
            this.studio.animMan.setAnimationTime(0);
        }
    }

    onPartsObjectComplete() {
        // find overview highlight
        this.overviewHL = this.studio.highlightMan.highlights.find(hl => hl.headline === "hl_1_title");

        this.studio.highlightMan.dispatcher.addEventListener('onstate', (event, state) => {
            console.log(state);
            if ((state === 0) && this.studio.highlightMan.__dbg_hl !== this.overviewHL) {
                this.studio.highlightMan.triggerHighlight(this.overviewHL.id);
            }
            //console.log(event, state);
        });
        console.log(this.overviewHL);
    }

    onLEDObjectComplete() {
        this.studio.scene.traverse(n => {
            if (n.name.startsWith('a_1901_SPRING_BRACKET_V003')) {
                n.visible = false;
                n.remove();
            }
        });
    }

    onObjectComplete() {
        this.studio.scene.traverse(n => {
            // remove a_1901_SPRING_BRACKET_V003
            if (n.name.startsWith('a_1901_SPRING_BRACKET_V003')) {
                n.visible = false;
                n.remove();
            }

            // get ref to all parts that need to be transparent at some time
            if (n.name.startsWith('a_1101_HOUSING_COVER_V014')) {
                this.housing = n;
                this.housing.material.targetOpacity = 1;
                this.housing.traverse(node => {
                    if (node.material) {
                        node.material.blending = 1;
                    }
                });
            }

            if (n.name.startsWith("G_20711_PANEL_PARADISE")) {
                this.cover = n;
                this.cover.material.targetOpacity = 1;
                this.cover.traverse(node => {
                    if (node.material) {
                        node.material.blending = 1;
                    }
                });
            }

            if (n.name.startsWith('Kern_Rolle_Remove')) {
                this.rolle = n.parent;
            }

            if (n.name.startsWith('LED_')) {
                const name = n.name;
                if (name !== "LED_2" && name !== "LED_6" && name !== "LED_7") {
                    n.children[0].visible = false;
                }
            }

            if (n.material && n.material.name && n.material.name.startsWith("Tuch")) {
                //console.log("TUCH MATERIAL", n.material);
                
                n.material = new MeshBasicMaterial({
                    color: 0xdddddd,
                    skinning: true,
                    morphTargets: true,
                    map: this.towelTex,
                    side: DoubleSide
                });
            }
        });

    }

    onLEDAnimation(event, data) {
        if (this.begin) {
            this.begin = false;
            this.studio.animMan.animationToggle(false);
            this.studio.animMan.setAnimationTime(0);
        }
    }

    onAnimation(event, data) {
        if (this.begin) {
            this.begin = false;
            this.studio.animMan.animationToggle(false);
            this.studio.animMan.setAnimationTime(0);
            if (this.autoPlay) {
                window.setTimeout(() => {
                    this.studio.animMan.animationToggle(true);
                }, 2000);
            } 
        }
        // set transparency of hood
        if (this.caseTransparentConditions.find(c => data.time >= c.from && data.time <= c.to)) {
            if (!this.isTransparent) {
                this.housing.material.targetOpacity = 0.2;
                this.housing.castShadow = false;
                this.housing.receiveShadow = false;
                this.cover.material.targetOpacity = 0.2;
                this.cover.castShadow = false;
                this.cover.receiveShadow = false;
            }
            this.isTransparent = true;
        } else {
            if (this.isTransparent) {
                this.housing.material.targetOpacity = 1.0;
                this.housing.castShadow = true;
                this.housing.receiveShadow = true;
                this.cover.material.targetOpacity = 1.0;
                this.cover.castShadow = true;
                this.cover.receiveShadow = true;
            }
            this.isTransparent = false;
        }

        // rolle

        if (this.rolle) {
            this.rolle.visible = data.time <= 29.93;
        }


    }

    blendOver(elem, delta) {
        if ( this.housing ) {
            if (Math.abs(this.housing.material.targetOpacity - this.housing.material.opacity) > 0.01) {
                console.log(this.housing.material.opacity,
                    this.housing.material.targetOpacity, delta);
                this.housing.material.opacity = this.interp(
                    this.housing.material.opacity,
                    this.housing.material.targetOpacity, delta, 6)
            } else if (this.housing.material.targetOpacity !== this.housing.material.opacity) {
                this.housing.material.opacity = this.housing.material.targetOpacity;
            }
        }
    }

    onRender() {
        const time = Date.now();
        const delta = time - this.lastRenderTime;

        if (this.lastRenderTime === 0) {
            this.lastRenderTime = time;
            return;
        }

        if (this.housing) {
            if (Math.abs(this.housing.material.targetOpacity - this.housing.material.opacity) > 0.01) {
                this.housing.material.opacity = this.interp(
                    this.housing.material.opacity,
                    this.housing.material.targetOpacity, delta, 6)
            } else if (this.housing.material.targetOpacity !== this.housing.material.opacity) {
                this.housing.material.opacity = this.housing.material.targetOpacity;
            }
        }

        if (this.cover) {
            if (Math.abs(this.cover.material.targetOpacity - this.cover.material.opacity) > 0.01) {
                this.cover.material.opacity = this.interp(
                    this.cover.material.opacity,
                    this.cover.material.targetOpacity, delta, 6)
            } else if (this.cover.material.targetOpacity !== this.cover.material.opacity) {
                this.cover.material.opacity = this.cover.material.targetOpacity;
            }
        }

        this.lastRenderTime = time;
    }
}


// WEBPACK FOOTER //
// ./src/js/plugins/CWSPLScene.js